import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Shield, Check, X, Edit } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { PEOOrganizationService } from '@/services/peo-organization.service';
import { ROLE_TYPE_LABELS, PERMISSION_LABELS } from '@/types/peo-organization.types';
import { BulkPermissionDialog } from './BulkPermissionDialog';
import { EditRoleDialog } from './EditRoleDialog';

interface PermissionMatrixProps {
  providerId: string;
}

export const PermissionMatrix: React.FC<PermissionMatrixProps> = ({
  providerId
}) => {
  const { data: roles, isLoading } = useQuery({
    queryKey: ['organizational-roles', providerId],
    queryFn: () => PEOOrganizationService.getOrganizationalRoles(providerId),
    enabled: !!providerId
  });

  const permissionColumns = [
    { key: 'can_view_applications', label: 'View Applications' },
    { key: 'can_create_applications', label: 'Create Applications' },
    { key: 'can_edit_applications', label: 'Edit Applications' },
    { key: 'can_delete_applications', label: 'Delete Applications' },
    { key: 'can_view_quotes', label: 'View Quotes' },
    { key: 'can_create_quotes', label: 'Create Quotes' },
    { key: 'can_edit_quotes', label: 'Edit Quotes' },
    { key: 'can_delete_quotes', label: 'Delete Quotes' },
    { key: 'can_view_analytics', label: 'View Analytics' },
    { key: 'can_manage_team', label: 'Manage Team' },
    { key: 'can_manage_roles', label: 'Manage Roles' },
  ];

  const PermissionIcon: React.FC<{ hasPermission: boolean }> = ({ hasPermission }) => (
    hasPermission ? (
      <Check className="h-4 w-4 text-green-600" />
    ) : (
      <X className="h-4 w-4 text-red-600" />
    )
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Permission Matrix</h2>
          <p className="text-muted-foreground">
            View and compare permissions across all organizational roles.
          </p>
        </div>
        <BulkPermissionDialog providerId={providerId}>
          <Button className="flex items-center gap-2">
            <Edit className="h-4 w-4" />
            Bulk Edit Permissions
          </Button>
        </BulkPermissionDialog>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-8">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-full"></div>
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
            </div>
          </CardContent>
        </Card>
      ) : !roles || roles.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No roles found</p>
            <p className="text-sm text-muted-foreground mt-2">
              Create some organizational roles to see the permission matrix.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Permission Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Roles</CardTitle>
                <Shield className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{roles.length}</div>
                <p className="text-xs text-muted-foreground">
                  Configured roles
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Admin Roles</CardTitle>
                <Shield className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {roles.filter(r => r.permission_level === 'all').length}
                </div>
                <p className="text-xs text-muted-foreground">
                  Full access roles
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Manager Roles</CardTitle>
                <Shield className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {roles.filter(r => r.can_manage_team).length}
                </div>
                <p className="text-xs text-muted-foreground">
                  Team management
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Restricted Roles</CardTitle>
                <Shield className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {roles.filter(r => r.permission_level === 'own_only').length}
                </div>
                <p className="text-xs text-muted-foreground">
                  Limited access
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Permission Matrix Table */}
          <Card>
            <CardHeader>
              <CardTitle>Permission Matrix</CardTitle>
              <CardDescription>
                Detailed view of permissions for each role. Green checkmarks indicate granted permissions.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-48">Role</TableHead>
                      <TableHead className="w-32">Permission Level</TableHead>
                      {permissionColumns.map((col) => (
                        <TableHead key={col.key} className="text-center min-w-32">
                          {col.label}
                        </TableHead>
                      ))}
                      <TableHead className="w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {roles.map((role) => (
                      <TableRow key={role.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{role.role_name}</div>
                            <div className="text-sm text-muted-foreground">
                              {ROLE_TYPE_LABELS[role.role_type]}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {PERMISSION_LABELS[role.permission_level]}
                          </Badge>
                        </TableCell>
                        {permissionColumns.map((col) => (
                          <TableCell key={col.key} className="text-center">
                            <PermissionIcon 
                              hasPermission={role[col.key as keyof typeof role] as boolean} 
                            />
                          </TableCell>
                        ))}
                        <TableCell>
                          <EditRoleDialog providerId={providerId} role={role}>
                            <Button variant="ghost" size="sm">
                              <Edit className="h-3 w-3" />
                            </Button>
                          </EditRoleDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Permission Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Permission Summary</CardTitle>
              <CardDescription>
                Quick overview of permission distribution across roles
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {permissionColumns.map((col) => {
                  const rolesWithPermission = roles.filter(role => 
                    role[col.key as keyof typeof role] as boolean
                  );
                  const percentage = Math.round((rolesWithPermission.length / roles.length) * 100);
                  
                  return (
                    <div key={col.key} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{col.label}</span>
                        <span className="text-sm text-muted-foreground">
                          {rolesWithPermission.length}/{roles.length}
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-primary h-2 rounded-full transition-all" 
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {percentage}% of roles have this permission
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};